<script setup>
import { ref, onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import api from "@/services/api";

import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const router = useRouter();

const email = ref('');
const loading = ref(false);
const message = ref('');
const error = ref('');
const warning = ref('');

const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  toggleDefaultLayout();
  body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  toggleDefaultLayout();
  body.classList.add("bg-gray-100");
});

const handleForgotPassword = async () => {
  if (!email.value) {
    error.value = 'Por favor ingresa tu email';
    return;
  }

  error.value = '';
  warning.value = '';
  message.value = '';
  loading.value = true;

  try {
    const response = await api.post('/auth/forgot-password', {
      email: email.value
    });

    if (response.data.status === 'success') {
      message.value = response.data.message;
      // Limpiar el formulario
      email.value = '';
    }
  } catch (err) {
    // Manejar específicamente el error de throttling (demasiados intentos)
    if (err.response?.data?.status === 'throttled') {
      warning.value = err.response.data.message || 'Ya se envió un enlace de restablecimiento. Por favor espera antes de solicitar otro.';
    }
    // Manejar específicamente el error 500 cuando el email no existe
    else if (err.response?.status === 500 && err.response?.data?.error?.includes('User unknown')) {
      error.value = 'El email ingresado no está registrado en nuestro sistema. Por favor, verifica el email o contacta al administrador.';
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else if (err.response?.data?.error) {
      error.value = 'Email no registrado. Por favor, verifica el email ingresado.';
    } else {
      error.value = 'Error al enviar el email. Inténtalo de nuevo.';
    }
  } finally {
    loading.value = false;
  }
};

const goToLogin = () => {
  router.push('/login');
};
</script>

<template>
  <div class="container top-0 position-sticky z-index-sticky">
    <div class="row">
      <div class="col-12">
        <!-- Navbar placeholder -->
      </div>
    </div>
  </div>
  <main class="main-content mt-0">
    <section>
      <div class="page-header min-vh-100">
        <div class="container">
          <div class="row">
            <div class="col-xl-4 col-lg-5 col-md-7 d-flex flex-column mx-lg-0 mx-auto">
              <div class="card card-plain">
                <div class="card-header pb-0 text-left">
                  <h4 class="font-weight-bolder">¿Olvidaste tu contraseña?</h4>
                  <p class="mb-0">Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña</p>
                </div>
                <div class="card-body pb-3">
                  <form v-if="!message" @submit.prevent="handleForgotPassword">
                    <label>Email</label>
                    <argon-input 
                      id="email" 
                      v-model="email" 
                      type="email" 
                      placeholder="Ingresa tu email" 
                      aria-label="Email"
                      :disabled="loading" />
                    
                    <div class="text-center">
                      <argon-button 
                        type="submit"
                        color="success" 
                        variant="gradient" 
                        full-width 
                        class="mt-4 mb-0"
                        :disabled="loading">
                        {{ loading ? 'Enviando...' : 'Enviar enlace de restablecimiento' }}
                      </argon-button>
                    </div>
                  </form>

                  <!-- Mensaje de éxito mejorado -->
                  <div v-if="message" class="text-center">
                    <div class="alert alert-success alert-dismissible fade show" role="alert">
                      <div class="d-flex align-items-start">
                        <i class="ni ni-check-bold me-2 mt-1 text-white"></i>
                        <div class="flex-grow-1">
                          <h6 class="alert-heading mb-1 text-white">¡Email enviado exitosamente!</h6>
                          <p class="mb-0 text-white small">{{ message }}</p>
                        </div>
                        <button type="button" class="btn-close btn-close-white" aria-label="Cerrar" @click="message = ''"></button>
                      </div>
                    </div>
                    <argon-button 
                      color="secondary" 
                      variant="outline" 
                      full-width 
                      class="mt-3"
                      @click="goToLogin">
                      Volver al login
                    </argon-button>
                  </div>

                  <!-- Mensaje de error mejorado -->
                  <div v-if="error" class="alert alert-danger alert-dismissible fade show mt-3" role="alert">
                    <div class="d-flex align-items-start">
                      <i class="ni ni-support-16 me-2 mt-1 text-white"></i>
                      <div class="flex-grow-1">
                        <h6 class="alert-heading mb-1 text-white">¡Oops! Algo salió mal</h6>
                        <p class="mb-0 text-white small">{{ error }}</p>
                      </div>
                      <button type="button" class="btn-close btn-close-white" aria-label="Cerrar" @click="error = ''"></button>
                    </div>
                  </div>

                  <!-- Mensaje de advertencia para throttling -->
                  <div v-if="warning" class="alert alert-warning alert-dismissible fade show mt-3" role="alert">
                    <div class="d-flex align-items-start">
                      <i class="ni ni-time-alarm me-2 mt-1 text-white"></i>
                      <div class="flex-grow-1">
                        <h6 class="alert-heading mb-1 text-white">¡Por favor espera!</h6>
                        <p class="mb-0 text-white small">{{ warning }}</p>
                      </div>
                      <button type="button" class="btn-close btn-close-white" aria-label="Cerrar" @click="warning = ''"></button>
                    </div>
                  </div>
                </div>
                <div class="px-1 pt-0 text-center card-footer px-lg-2">
                  <p class="mx-auto mb-4 text-sm">
                    ¿Recordaste tu contraseña?
                    <router-link
:to="{ name: 'Login' }"
                      class="text-success text-gradient font-weight-bold">Volver al login</router-link>
                  </p>
                </div>
              </div>
            </div>
            <div
              class="col-6 d-lg-flex d-none h-100 my-auto pe-0 position-absolute top-0 end-0 text-center justify-content-center flex-column">
              <div
                class="position-relative bg-gradient-primary h-100 m-3 px-7 border-radius-lg d-flex flex-column justify-content-center overflow-hidden">
                <span class="mask bg-primary opacity-4"></span>
                <h4 class="mt-5 text-white font-weight-bolder position-relative">
                  Recuperar acceso
                </h4>
                <p class="text-white position-relative">
                  Te ayudamos a recuperar el acceso a tu cuenta de forma segura
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.alert {
  padding: 1rem;
  border-radius: 0.375rem;
  margin: 0;
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.alert-success {
  background: linear-gradient(87deg, #2dce89 0, #2dcecc 100%);
  color: white;
}

.alert-danger {
  background: linear-gradient(87deg, #f5365c 0, #f56036 100%);
  color: white;
}

.alert-warning {
  background: linear-gradient(87deg, #fb6340 0, #fbb140 100%);
  color: white;
}

.alert-heading {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
  opacity: 0.8;
  background: none;
  border: none;
  font-size: 1rem;
  padding: 0.25rem;
}

.btn-close-white:hover {
  opacity: 1;
}

.alert .ni {
  font-size: 1rem;
  opacity: 0.9;
}

.fade.show {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.small {
  font-size: 0.8rem;
  line-height: 1.4;
}
</style>